import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import ScoreBadge from "../components/ScoreBadge";
import { Button } from "../components/ui/button";
import { ArrowUpRight, Target, Users, Send, MailOpen, MessageSquare, CalendarCheck, TrendingUp, Linkedin, Flame, ExternalLink, RefreshCw, Sparkles, Mail, Calendar, Search, Workflow, X, Check, Zap, AlertOctagon } from "lucide-react";
import LeadDrawer from "../components/LeadDrawer";
import AIDraftModal from "../components/AIDraftModal";
import SequencePreviewModal from "../components/SequencePreviewModal";
import { toast } from "sonner";

const KPI = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <div className="bg-card border border-border p-6 lift fade-up" style={{ borderRadius: 2 }} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="overline">{label}</div>
        <div className="font-serif text-4xl lg:text-5xl mt-3 tracking-tight" style={{ fontWeight: 350 }}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
      </div>
      {Icon && <Icon className={`h-5 w-5 ${accent ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />}
    </div>
  </div>
);

const ACTION_ICONS = {
  reply_to_inbound: MessageSquare,
  send_pre_meeting_brief: Calendar,
  reply_to_trigger: Flame,
  first_email: Mail,
  linkedin_dm_first: Linkedin,
  linkedin_followup: Linkedin,
  research_lead: Search,
  resume_paused_seq: Workflow,
  sequence_task: Workflow,
};

function QuotaRing({ target, completed, pct }) {
  const r = 16;
  const c = 2 * Math.PI * r;
  const p = Math.min(1, (completed || 0) / Math.max(1, target));
  const off = c * (1 - p);
  const isHit = completed >= target;
  return (
    <div className="relative h-10 w-10 shrink-0" data-testid="coach-quota-ring" title={`${completed} / ${target} actions today`}>
      <svg viewBox="0 0 40 40" className="h-10 w-10 -rotate-90">
        <circle cx="20" cy="20" r={r} stroke="hsl(var(--border))" strokeWidth="3" fill="none" />
        <circle cx="20" cy="20" r={r} stroke={isHit ? "hsl(var(--success))" : "hsl(var(--primary))"} strokeWidth="3" fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset .4s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] font-medium">
        {completed}/{target}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [quota, setQuota] = useState(null);
  const [coachLoading, setCoachLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [drawerLead, setDrawerLead] = useState(null);
  const [drawerTab, setDrawerTab] = useState("research");
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [draftModalLead, setDraftModalLead] = useState(null);
  const [draftModalKind, setDraftModalKind] = useState("insight");
  const [draftModalExtra, setDraftModalExtra] = useState("");
  const [draftModalEmailId, setDraftModalEmailId] = useState(null);
  const [draftModalAuto, setDraftModalAuto] = useState(false);
  const [previewLead, setPreviewLead] = useState(null);
  const [researchingBulk, setResearchingBulk] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (hash.includes("runmyday=1")) {
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const summary = ["sent", "drafted", "voicemails", "ai_calls", "researched"]
        .map((k) => (params.get(k) ? `${params.get(k)} ${k}` : null))
        .filter(Boolean).join(" · ");
      toast.success(`Magic-link run complete${summary ? " · " + summary : ""}`);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const load = async () => {
    try {
      const [k, c] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/coach/today", { params: { limit: 5 } }),
      ]);
      setKpis(k.data);
      setActions(c.data?.actions || []);
      setQuota(c.data?.quota || null);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setCoachLoading(false);
    }
  };

  const refreshCoach = async () => {
    setCoachLoading(true);
    try {
      const { data } = await api.get("/coach/today", { params: { limit: 5 } });
      setActions(data?.actions || []);
      setQuota(data?.quota || null);
    } catch {}
    setCoachLoading(false);
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    try {
      const { data } = await api.post("/seed");
      toast.success(`Seeded ${data.seeded} CFO leads`);
      load();
    } catch {
      toast.error("Seed failed");
    }
  };

  const scanTriggers = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/triggers/scan-now");
      toast.success(`Scanned ${data.leads_scanned} leads · ${data.items_saved} new triggers · ${data.hot_leads} 🔥 hot`);
      await refreshCoach();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const dismissAction = async (action_id) => {
    setActions((arr) => arr.filter((a) => a.action_id !== action_id));
    try { await api.post(`/coach/dismiss/${action_id}`); } catch {}
  };

  const completeAction = async (action) => {
    setActions((arr) => arr.filter((a) => a.action_id !== action.action_id));
    try {
      await api.post(`/coach/complete/${action.action_id}`);
      // If it's an underlying sequence task, also close the task
      if (action.type === "sequence_task" && action.extra?.task_id) {
        await api.patch(`/sequence-tasks/${action.extra.task_id}`, { status: "completed" });
      }
      toast.success("Marked done");
    } catch {}
  };

  const runAction = async (action) => {
    const lead = action.lead;
    if (action.cta_kind === "draft_email") {
      // Open AI Draft modal pre-loaded with kind + extra context
      setDraftModalLead(lead);
      setDraftModalKind(action.extra?.email_kind || action.cta_payload?.email_kind || "insight");
      // Provide extra instructions for trigger-based emails
      let extra = "";
      if (action.type === "reply_to_trigger" && action.extra) {
        extra = `Reference this trigger: "${action.extra.title}". Angle: ${action.extra.angle}. Source URL: ${action.extra.url}.`;
      } else if (action.type === "reply_to_inbound" && action.extra) {
        extra = `This is a reply to an inbound: subject="${action.extra.subject || ''}". Inbound body: ${action.extra.inbound_text || ''}`;
      }
      setDraftModalExtra(extra);
      // If this trigger was auto-drafted, load that email directly; otherwise generate fresh on open
      setDraftModalEmailId(action.extra?.email_id || null);
      setDraftModalAuto(!action.extra?.email_id && action.type === "reply_to_trigger");
      setDraftModalOpen(true);
    } else if (action.cta_kind === "open_lead") {
      const tab = action.cta_payload?.tab || "research";
      setDrawerTab(tab);
      setDrawerLead(lead);
      // If research auto-trigger requested, kick it off
      if (action.cta_payload?.auto === "research") {
        try {
          await api.post(`/leads/${lead.lead_id}/research`);
          toast.success("Research running…");
        } catch {}
      }
    }
  };

  return (
    <Layout
      title="Command Centre"
      subtitle="Dashboard"
      actions={
        <>
          <Button variant="outline" onClick={seed} data-testid="seed-btn" style={{ borderRadius: 2 }}>Load sample CFO leads</Button>
          <Button variant="outline" onClick={async () => {
            try {
              const { data } = await api.post("/runner/run-now");
              const c = data.counters || {};
              const summary = Object.entries(c).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(" · ") || "Nothing to do right now";
              toast.success(`Run my day · ${summary}`);
              refreshCoach();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Run failed");
            }
          }} data-testid="dashboard-run-my-day" style={{ borderRadius: 2 }}>
            <Zap className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Run my day
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              setResearchingBulk(true);
              const { data } = await api.post("/ai/research-bulk", {});
              if (data.total === 0) {
                toast.success("All leads already have AI research");
              } else {
                toast.success(`AI research complete · ${data.researched}/${data.total} leads enriched`);
              }
              load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Bulk research failed");
            } finally {
              setResearchingBulk(false);
            }
          }} disabled={researchingBulk} data-testid="dashboard-research-all" style={{ borderRadius: 2 }}>
            <Sparkles className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {researchingBulk ? "Researching…" : "Research all leads"}
          </Button>
          <Button onClick={() => navigate("/leads")} data-testid="go-to-leads" style={{ borderRadius: 2 }}>Open leads <ArrowUpRight className="h-4 w-4 ml-1" strokeWidth={1.5} /></Button>
        </>
      }
    >
      {loading ? (
        <div className="font-mono text-sm text-muted-foreground">Loading…</div>
      ) : !kpis ? (
        <div className="font-mono text-sm text-muted-foreground">Couldn't load dashboard KPIs. <button onClick={load} className="underline">Retry</button></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI label="Meetings this week" value={kpis.meetings_week} sub={`Target ${kpis.meetings_target_week}/wk`} icon={CalendarCheck} accent testid="kpi-meetings-week" />
            <KPI label="Leads in DB" value={kpis.leads_total} sub={`+${kpis.leads_week} this week`} icon={Users} testid="kpi-leads-total" />
            <KPI label="High priority" value={kpis.leads_high_priority} sub="Score 80-100" icon={Target} testid="kpi-high-priority" />
            <KPI label="Active pipeline" value={kpis.pipeline_count} sub="Qualified → Opportunity" icon={TrendingUp} testid="kpi-pipeline" />
            <KPI label="Emails sent" value={kpis.emails_sent} sub={`${kpis.emails_sent_week} this week`} icon={Send} testid="kpi-emails-sent" />
            <KPI label="Open rate" value={`${kpis.open_rate}%`} sub="Industry target 40%+" icon={MailOpen} testid="kpi-open-rate" />
            <KPI label="Reply rate" value={`${kpis.reply_rate}%`} sub="Target 5-10%" icon={MessageSquare} testid="kpi-reply-rate" />
            <KPI label="LinkedIn touches" value={kpis.linkedin_week} sub={`${kpis.linkedin_acceptance_rate}% accept rate`} icon={Linkedin} testid="kpi-linkedin" />
            <KPI label="Clients won" value={kpis.clients_won} sub={`SQL ${kpis.sql_conversion_rate}%`} icon={ArrowUpRight} testid="kpi-clients-won" />
            {kpis.bounced_total > 0 && (
              <Link to="/leads?filter=bounced" className="block" data-testid="kpi-bounced-link">
                <div className="bg-card border border-rose-300 p-6 lift fade-up hover:border-rose-400 transition-colors" style={{ borderRadius: 2 }} data-testid="kpi-bounced">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="overline text-rose-700">Bounced (dead emails)</div>
                      <div className="font-serif text-4xl lg:text-5xl mt-3 tracking-tight text-rose-700" style={{ fontWeight: 350 }}>{kpis.bounced_total}</div>
                      <div className="text-xs text-rose-600/80 mt-2">
                        +{kpis.bounced_week} this week · {kpis.bounce_rate}% of DB · click to view
                      </div>
                    </div>
                    <AlertOctagon className="h-5 w-5 text-rose-500" strokeWidth={1.5} />
                  </div>
                </div>
              </Link>
            )}
          </div>

          {/* Daily outreach — answers "how many InMails / Emails went out today?" */}
          <DailyOutreach />

          {/* Cold-email diagnosis — root-cause card when Instantly is 0/day */}
          <ColdEmailDiagnosis />

          {/* Silence history — last 5 outages / recovery events */}
          <SilenceHistory />

          {/* Warmup health */}
          <WarmupHealth />

          {/* Sales Coach — Today's hot list */}
          <div className="mt-10 bg-card border border-border" style={{ borderRadius: 2 }} data-testid="coach-card">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.8} />
                <div>
                  <div className="overline">Today's hot list · highest-ROI actions</div>
                  {quota && (
                    <div className="text-xs text-muted-foreground mt-0.5 font-mono" data-testid="coach-quota-text">
                      {quota.completed} of {quota.target} done today
                      {quota.remaining > 0 ? ` · ${quota.remaining} to hit your daily target` : " · 🎯 target hit"}
                    </div>
                  )}
                </div>
                {actions.length > 0 && <span className="text-xs font-mono text-muted-foreground">{actions.length}</span>}
              </div>
              <div className="flex items-center gap-3">
                {quota && (
                  <QuotaRing target={quota.target} completed={quota.completed} pct={quota.pct} />
                )}
                <Button size="sm" variant="outline" onClick={scanTriggers} disabled={scanning} data-testid="dashboard-scan-triggers" style={{ borderRadius: 2 }}>
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${scanning ? "animate-spin" : ""}`} strokeWidth={1.5} /> {scanning ? "Scanning…" : "Scan triggers"}
                </Button>
                <Button size="sm" variant="outline" onClick={refreshCoach} disabled={coachLoading} data-testid="coach-refresh" style={{ borderRadius: 2 }}>
                  <RefreshCw className={`h-3.5 w-3.5 ${coachLoading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                </Button>
              </div>
            </div>
            <div>
              {actions.length === 0 && !coachLoading && (
                <div className="px-6 py-10 text-center text-sm text-muted-foreground" data-testid="coach-empty">
                  Nothing urgent right now. Click <strong className="text-foreground">Scan triggers</strong> to surface fresh news signals, or seed leads to begin.
                </div>
              )}
              {actions.map((a, i) => {
                const Icon = ACTION_ICONS[a.type] || Sparkles;
                return (
                  <div key={a.action_id} className="px-6 py-4 border-b border-border last:border-0 flex items-start gap-4 hover:bg-secondary/40 transition-colors group" data-testid={`coach-action-${a.action_id}`}>
                    <div className="text-3xl font-serif tracking-tight text-muted-foreground/40 w-8 shrink-0 leading-none mt-0.5" style={{ fontWeight: 350 }}>{i + 1}</div>
                    <div className={`h-9 w-9 flex items-center justify-center shrink-0 ${a.lead?.has_hot_trigger || a.type === "reply_to_trigger" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`} style={{ borderRadius: 2 }}>
                      <Icon className="h-4 w-4" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{a.label}</span>
                        {a.lead?.has_hot_trigger && a.type !== "reply_to_trigger" && <Flame className="h-3 w-3 text-primary" strokeWidth={1.8} />}
                        <span className="text-[10px] font-mono text-muted-foreground/60">priority {a.priority}</span>
                      </div>
                      <div className="font-medium mb-1">{a.headline}</div>
                      {a.reasoning && <div className="text-sm text-muted-foreground italic line-clamp-2">{a.reasoning}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 opacity-100 lg:opacity-70 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" onClick={() => runAction(a)} data-testid={`coach-cta-${a.action_id}`} style={{ borderRadius: 2 }}>
                        {a.cta_label}
                      </Button>
                      <button onClick={() => completeAction(a)} className="text-muted-foreground hover:text-[hsl(var(--success))] p-1" title="Mark done" data-testid={`coach-complete-${a.action_id}`}>
                        <Check className="h-4 w-4" strokeWidth={1.8} />
                      </button>
                      <button onClick={() => dismissAction(a.action_id)} className="text-muted-foreground hover:text-foreground p-1" title="Dismiss for today" data-testid={`coach-dismiss-${a.action_id}`}>
                        <X className="h-4 w-4" strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pipeline */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-card border border-border p-6 fade-up" style={{ borderRadius: 2 }}>
              <div className="overline mb-4">Pipeline by stage</div>
              <div className="space-y-2">
                {Object.entries(kpis.pipeline_by_stage).map(([stage, count]) => {
                  const max = Math.max(...Object.values(kpis.pipeline_by_stage), 1);
                  const pct = (count / max) * 100;
                  return (
                    <div key={stage} className="flex items-center gap-4" data-testid={`pipeline-row-${stage}`}>
                      <div className="w-44 text-sm capitalize">{stage.replace(/_/g, " ")}</div>
                      <div className="flex-1 h-2 bg-secondary relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${pct}%`, transition: "width 0.4s ease" }} />
                      </div>
                      <div className="w-10 text-right font-mono text-sm">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-card border border-border p-6 fade-up" style={{ borderRadius: 2 }}>
              <div className="overline mb-4">Top industries</div>
              <div className="space-y-3">
                {kpis.top_industries.length === 0 && <div className="text-sm text-muted-foreground">No data yet</div>}
                {kpis.top_industries.map((it) => (
                  <div key={it.industry} className="flex items-center justify-between" data-testid={`industry-${it.industry}`}>
                    <div className="text-sm">{it.industry}</div>
                    <div className="font-mono text-sm">{it.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top leads */}
          <div className="mt-10 bg-card border border-border" style={{ borderRadius: 2 }}>
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="overline">Highest scoring leads</div>
              <button onClick={() => navigate("/leads")} className="text-xs text-muted-foreground hover:text-foreground" data-testid="view-all-leads">View all →</button>
            </div>
            <div>
              {kpis.top_leads.length === 0 && (
                <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                  No leads yet — click "Load sample CFO leads" to populate the database.
                </div>
              )}
              {kpis.top_leads.map((l) => (
                <div key={l.lead_id} className="tbl-row px-6 py-4 flex items-center gap-4 cursor-pointer" onClick={() => navigate(`/leads?focus=${l.lead_id}`)} data-testid={`top-lead-${l.lead_id}`}>
                  <div className="h-9 w-9 bg-secondary text-foreground flex items-center justify-center text-sm font-medium" style={{ borderRadius: 2 }}>
                    {l.full_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{l.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{l.title} · {l.company} · {l.industry}</div>
                  </div>
                  <ScoreBadge score={l.score} band={l.score_band} />
                  <button
                    onClick={(e) => { e.stopPropagation(); setPreviewLead(l); }}
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 py-1 border border-border"
                    style={{ borderRadius: 2 }}
                    data-testid={`preview-sequence-${l.lead_id}`}
                    title="Preview the AI-drafted 11-step sequence for this lead"
                  >
                    <Sparkles className="h-3 w-3" strokeWidth={1.5} /> Preview
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <LeadDrawer
        lead={drawerLead}
        defaultTab={drawerTab}
        onClose={() => setDrawerLead(null)}
        onUpdated={(updated) => { setDrawerLead(updated); load(); }}
      />
      <AIDraftModal
        open={draftModalOpen}
        onClose={() => setDraftModalOpen(false)}
        lead={draftModalLead}
        defaultKind={draftModalKind}
        defaultExtra={draftModalExtra}
        existingEmailId={draftModalEmailId}
        autoGenerate={draftModalAuto}
        onSaved={() => { setDraftModalOpen(false); refreshCoach(); load(); }}
      />
      <SequencePreviewModal
        open={!!previewLead}
        onClose={() => setPreviewLead(null)}
        lead={previewLead}
        autoGenerate={true}
      />
    </Layout>
  );
}


function WarmupHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get("/deliverability/overview")
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    load();
    const t = setInterval(load, 60 * 60 * 1000);  // refresh every hour
    return () => clearInterval(t);
  }, []);

  if (loading) return null;
  if (!data || !data.connected) return null;
  if ((data.mailboxes || []).length === 0) return null;

  const mailboxes = data.mailboxes;
  const avg = data.totals.avg_warmup;
  const lowest = Math.min(...mailboxes.map((m) => m.warmup_score));
  const daysWarming = Math.max(...mailboxes.map((m) => m.days_warming || 0));
  const liveSendReady = daysWarming >= 21 && lowest >= 80;
  const liveSendETA = (() => {
    const targetDays = 21;
    if (daysWarming >= targetDays) return null;
    const d = new Date();
    d.setDate(d.getDate() + (targetDays - daysWarming));
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  })();
  const liveCapacity = mailboxes.reduce((s, m) => s + (m.daily_limit || 0), 0);

  const bandColor = (score) =>
    score >= 90 ? "text-[hsl(var(--success))]"
    : score >= 75 ? "text-[hsl(var(--success))]"
    : score >= 50 ? "text-[hsl(var(--warning))]"
    : "text-destructive";

  return (
    <div className="mt-8 bg-card border border-border" style={{ borderRadius: 2 }} data-testid="warmup-health-card">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MailOpen className="h-4 w-4 text-primary" strokeWidth={1.8} />
          <div>
            <div className="overline">Inbox warmup health</div>
            <div className="text-xs text-muted-foreground mt-0.5 font-mono">
              {mailboxes.length} mailbox{mailboxes.length === 1 ? "" : "es"} · day {daysWarming} of warmup ·{" "}
              {liveSendReady ? (
                <span className="text-[hsl(var(--success))]">live-send ready · capacity {liveCapacity}/day</span>
              ) : (
                <>cold-emails go live ~<strong className="text-foreground">{liveSendETA}</strong> (target day 21)</>
              )}
            </div>
          </div>
        </div>
        <Link to="/deliverability" className="text-xs underline hover:text-foreground" data-testid="warmup-health-detail-link">View details →</Link>
      </div>

      <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-border">
        <div>
          <div className="overline">Avg score</div>
          <div className={`font-mono text-2xl ${bandColor(avg)}`} style={{ fontWeight: 450 }}>{avg}<span className="text-xs text-muted-foreground">/100</span></div>
        </div>
        <div>
          <div className="overline">Lowest score</div>
          <div className={`font-mono text-2xl ${bandColor(lowest)}`} style={{ fontWeight: 450 }}>{lowest}<span className="text-xs text-muted-foreground">/100</span></div>
        </div>
        <div>
          <div className="overline">Days warming</div>
          <div className="font-mono text-2xl text-foreground" style={{ fontWeight: 450 }}>{daysWarming}<span className="text-xs text-muted-foreground">/21</span></div>
        </div>
        <div>
          <div className="overline">Live capacity</div>
          <div className="font-mono text-2xl text-foreground" style={{ fontWeight: 450 }}>{liveCapacity}<span className="text-xs text-muted-foreground">/day</span></div>
        </div>
      </div>

      <div className="px-6 py-3">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {mailboxes.map((m) => (
            <div key={m.email} className="flex items-center justify-between py-1.5 text-xs" data-testid={`warmup-mb-${m.email}`}>
              <div className="min-w-0 flex-1">
                <div className="font-mono truncate">{m.email}</div>
                <div className="text-[10px] text-muted-foreground">{m.warmup_status} · {m.daily_limit}/day limit · {m.days_warming || 0}d</div>
              </div>
              <div className={`font-mono text-lg ml-3 ${bandColor(m.warmup_score)}`} style={{ fontWeight: 450 }}>
                {m.warmup_score}<span className="text-[10px] text-muted-foreground">/100</span>
              </div>
            </div>
          ))}
        </div>
        {lowest < 80 && (
          <div className="mt-3 text-xs text-[hsl(var(--warning))]">
            ⚠ One or more mailboxes scoring below 80. Do not send live cold emails from those until they recover.
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Daily outreach widget ------------------------------------------------
// Shows today's count for each channel + 7-day sparkline + breakdown table.
// Answers: "how many InMails and emails are going every day?".

function Spark({ points, color = "currentColor" }) {
  // Inline SVG sparkline. `points` is an array of numbers (oldest → newest).
  const w = 88, h = 26, pad = 2;
  const max = Math.max(1, ...points);
  const step = (w - pad * 2) / Math.max(1, points.length - 1);
  const path = points
    .map((v, i) => {
      const x = pad + i * step;
      const y = h - pad - (v / max) * (h - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-6 w-22" preserveAspectRatio="none">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChannelTile({ label, value, avg, points, color, testid }) {
  const delta = value - (avg || 0);
  const deltaTxt = avg
    ? `${delta >= 0 ? "+" : ""}${delta.toFixed(1)} vs 7d avg ${avg.toFixed(1)}`
    : `7d avg ${(avg || 0).toFixed(1)}`;
  return (
    <div className="bg-card border border-border p-4 fade-up" style={{ borderRadius: 2 }} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="overline">{label}</div>
          <div className="font-serif text-3xl mt-2 tracking-tight" style={{ fontWeight: 350 }}>{value}</div>
          <div className="text-[11px] text-muted-foreground mt-1 font-mono">{deltaTxt}</div>
        </div>
        <div style={{ color }} className="shrink-0">
          <Spark points={points} color={color} />
        </div>
      </div>
    </div>
  );
}

function SilenceHistory() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/silence-history", { params: { limit: 5 } })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!events.length) return null;

  const KIND_META = {
    silence_24h_watchdog: { label: "Silence 24h", tone: "rose" },
    silence_24h_false_alarm_recovered: { label: "False-alarm avoided", tone: "emerald" },
    silence_alert: { label: "No touches by 13:00", tone: "rose" },
    watchdog_alert: { label: "Scheduler stall", tone: "amber" },
    campaign_resumed: { label: "Campaign resumed", tone: "emerald" },
    linkedin_reset: { label: "LinkedIn reset", tone: "sky" },
    linkedin_auto_reset: { label: "Daily auto-reset", tone: "sky" },
  };

  const relativeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const h = diff / 3_600_000;
    if (h < 1) return `${Math.max(1, Math.round(diff / 60_000))}m ago`;
    if (h < 24) return `${Math.round(h)}h ago`;
    const d = h / 24;
    if (d < 7) return `${Math.round(d)}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  const summarise = (e) => {
    if (e.kind === "campaign_resumed" && e.resumed_campaign) {
      const hrs = e.paused_for_hours ? ` after ${Math.round(e.paused_for_hours)}h paused` : "";
      return `${e.resumed_campaign.name} · bumped ${e.leads_bumped || 0} stale leads${hrs}`;
    }
    if (e.kind === "linkedin_reset") {
      return "User hit the 1-click LinkedIn state reset.";
    }
    if (e.kind === "linkedin_auto_reset") {
      const age = e.unipile_age_hours_at_reset;
      const ageLabel = typeof age === "number" ? ` (Unipile silent ${Math.round(age)}h)` : "";
      return `Daily 03:00 Sydney cron self-healed LinkedIn${ageLabel} · cleared ${e.inmail_leads_cleared || 0} InMail + ${e.invite_backoffs_cleared || 0} invite flags`;
    }
    if (e.kind === "silence_24h_false_alarm_recovered") {
      return "Alert about to fire but state recovered before send — skipped.";
    }
    const reds = (e.red_items || []).map((r) => `${r.channel}: ${(r.detail || "").slice(0, 80)}`);
    if (reds.length) return reds.join(" · ");
    if (e.silence_snapshot) {
      const parts = [];
      if (e.silence_snapshot.unipile?.status === "red")
        parts.push(`Unipile ${Math.round(e.silence_snapshot.unipile.age_hours || 0)}h silent`);
      if (e.silence_snapshot.apollo?.status === "red")
        parts.push(`Apollo ${Math.round(e.silence_snapshot.apollo.age_hours || 0)}h silent`);
      return parts.join(" · ");
    }
    return "";
  };

  return (
    <div
      className="mt-8 border border-border bg-card"
      style={{ borderRadius: 2 }}
      data-testid="silence-history-card"
    >
      <div className="px-5 py-4 border-b border-border/60">
        <div className="overline">Recent outages · last 5</div>
        <div className="text-sm text-muted-foreground mt-0.5">
          Silence-watchdog + scheduler-stall history so recurring problems become visible.
        </div>
      </div>
      <div>
        {events.slice(0, 5).map((e) => {
          const meta = KIND_META[e.kind] || { label: e.kind, tone: "slate" };
          const toneClasses = {
            rose: "bg-rose-500",
            amber: "bg-amber-500",
            emerald: "bg-emerald-500",
            sky: "bg-sky-500",
            slate: "bg-slate-500",
          }[meta.tone];
          return (
            <div
              key={e.event_id}
              className="px-5 py-3 flex items-start gap-3 border-t border-border/40 first:border-t-0"
              data-testid={`silence-history-row-${e.event_id}`}
            >
              <div
                className={`mt-1 h-2 w-2 shrink-0 ${toneClasses}`}
                style={{ borderRadius: 999 }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-medium text-sm">{meta.label}</div>
                  <div className="text-xs text-muted-foreground font-mono">{relativeAgo(e.created_at)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 break-words">
                  {summarise(e)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ColdEmailDiagnosis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () =>
    api
      .get("/dashboard/cold-email-diagnosis")
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));

  useEffect(() => { load(); }, []);

  if (loading || !data) return null;

  const isGood = data.verdict === "all_good_check_deliverability";
  const isNoKey = data.verdict === "no_api_key";
  if (isNoKey) return null;  // Silent for users without Instantly configured

  const rerun = async () => {
    setRunning(true);
    await load();
    setRunning(false);
  };

  const label = (data.verdict || "").replace(/_/g, " ");
  const bar = isGood
    ? "border-emerald-300 bg-emerald-50/50"
    : "border-rose-300 bg-rose-50/60";
  const dotClass = isGood
    ? "bg-emerald-500"
    : "bg-rose-500";

  return (
    <div
      className={`mt-8 border ${bar}`}
      style={{ borderRadius: 2 }}
      data-testid="cold-email-diagnosis-card"
    >
      <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-2.5 w-2.5 shrink-0 ${dotClass}`} style={{ borderRadius: 999 }} />
          <div className="min-w-0">
            <div className="overline">Cold-email root cause</div>
            <div className="font-medium text-sm capitalize">{label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-xs text-muted-foreground font-mono">
            {data.counts?.campaigns_active}/{data.counts?.campaigns_total} campaigns active · {data.counts?.mailboxes_green_managed}/{data.counts?.mailboxes_total} mailboxes warm
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={rerun}
            disabled={running}
            data-testid="cold-email-diagnosis-rerun"
          >
            {running ? "Checking…" : "Re-run"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            data-testid="cold-email-diagnosis-toggle"
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border/40 px-5 py-4 space-y-4 bg-white/60">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Why</div>
            <div className="text-sm">{data.reason}</div>
          </div>

          {data.recommendations?.length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Recommendations</div>
              <ul className="list-disc list-inside text-sm space-y-1">
                {data.recommendations.map((r, i) => (
                  <li key={i} data-testid={`cold-email-rec-${i}`}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {data.campaigns?.length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Campaigns</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.campaigns.map((c) => (
                  <div
                    key={c.id}
                    className="border border-border bg-card px-3 py-2 text-xs"
                    style={{ borderRadius: 2 }}
                    data-testid={`cold-email-campaign-${c.id}`}
                  >
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-muted-foreground mt-0.5">
                      <span className={c.status === "active" ? "text-emerald-700" : (c.status === "paused" ? "text-rose-700" : "text-muted-foreground")}>
                        {c.status}
                      </span>
                      {" · "}{c.leads_count ?? "?"} leads
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.daily?.length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Last 7 days · sent</div>
              <div className="flex items-end gap-1 h-14">
                {data.daily.map((d) => {
                  const max = Math.max(1, ...data.daily.map((x) => x.sent));
                  const h = Math.max(4, Math.round((d.sent / max) * 48));
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full bg-primary/70"
                        style={{ height: `${h}px`, borderRadius: 1 }}
                        title={`${d.date}: ${d.sent}`}
                      />
                      <div className="text-[10px] text-muted-foreground font-mono">{d.sent}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function DailyOutreach() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get("/dashboard/outreach-daily")
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (loading) return null;
  if (!data) return null;

  const series = data.series || [];
  const todayTotal = data.today.total;
  const avgTotal = data.seven_day_avg.total;

  const fmtDay = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
  };

  return (
    <div className="mt-8 bg-card border border-border" style={{ borderRadius: 2 }} data-testid="daily-outreach-card">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Send className="h-4 w-4 text-primary" strokeWidth={1.8} />
          <div>
            <div className="overline">Today's outreach</div>
            <div className="text-xs text-muted-foreground mt-0.5 font-mono">
              <span data-testid="daily-outreach-today-total">{todayTotal}</span> touches today · 7-day avg <span data-testid="daily-outreach-7d-avg">{avgTotal}</span> · yesterday {data.yesterday.total}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ChannelTile
          label="InMails"
          value={data.today.inmails}
          avg={data.seven_day_avg.inmails}
          points={series.map((r) => r.inmails)}
          color="hsl(var(--primary))"
          testid="daily-tile-inmails"
        />
        <ChannelTile
          label="Invites"
          value={data.today.invites}
          avg={data.seven_day_avg.invites}
          points={series.map((r) => r.invites)}
          color="#0a66c2"
          testid="daily-tile-invites"
        />
        <ChannelTile
          label="DMs"
          value={data.today.dms}
          avg={data.seven_day_avg.dms}
          points={series.map((r) => r.dms)}
          color="#7c3aed"
          testid="daily-tile-dms"
        />
        <ChannelTile
          label="Emails"
          value={data.today.emails}
          avg={data.seven_day_avg.emails}
          points={series.map((r) => r.emails)}
          color="hsl(var(--success))"
          testid="daily-tile-emails"
        />
      </div>

      <div className="px-6 pb-4">
        <table className="w-full text-xs font-mono" data-testid="daily-outreach-table">
          <thead>
            <tr className="text-muted-foreground border-b border-border">
              <th className="text-left py-1.5 font-normal">Day</th>
              <th className="text-right py-1.5 font-normal">InMails</th>
              <th className="text-right py-1.5 font-normal">Invites</th>
              <th className="text-right py-1.5 font-normal">DMs</th>
              <th className="text-right py-1.5 font-normal">Emails</th>
              <th className="text-right py-1.5 font-normal">Total</th>
            </tr>
          </thead>
          <tbody>
            {series.map((row) => {
              const total = row.inmails + row.invites + row.dms + row.emails;
              const isToday = row.day === series[series.length - 1].day;
              return (
                <tr key={row.day} className={`border-b border-border/40 ${isToday ? "text-foreground" : "text-muted-foreground"}`} data-testid={`daily-outreach-row-${row.day}`}>
                  <td className="py-1.5">{fmtDay(row.day)}{isToday ? " · today" : ""}</td>
                  <td className="text-right py-1.5">{row.inmails}</td>
                  <td className="text-right py-1.5">{row.invites}</td>
                  <td className="text-right py-1.5">{row.dms}</td>
                  <td className="text-right py-1.5">{row.emails}</td>
                  <td className="text-right py-1.5">{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

